import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import EventRepeatRounded from '@mui/icons-material/EventRepeatRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import { Box, Button, Card, Link, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { RequireCapability } from '../../auth/RequireAuth';
import { EmptyState, ErrorState } from '../../components/states';
import { useNotify } from '../../lib/notify';
import { CodesPanel, type CodesState } from '../activation-codes/CodesPanel';
import { ActivityTimeline } from './ActivityTimeline';
import { fetchTenant, tenantKeys, type TenantDetail } from './api';
import { OverviewTab } from './OverviewTab';
import { ReasonDialog, TenantAvatar, TenantStatusChip } from './parts';
import { MenusTab } from './MenusTab';
import { UsersTab } from './UsersTab';
import { TenantBackupButton } from './TenantBackupButton';
import { ExtendDialog, SubscriptionTab } from './SubscriptionTab';
import { SupportTab } from './SupportTab';
import { PluginTenantTab } from '../plugins/PluginTenantTab';
import { WhatsappTenantTab } from '../whatsapp/WhatsappTenantTab';
import { TenantMessagesTab } from '../messages/TenantMessagesTab';
import { MessageButtons } from '../messages/MessageButtons';

type TabKey =
  | 'overview'
  | 'subscription'
  | 'whatsapp'
  | 'foodpanda'
  | 'email'
  | 'messages'
  | 'codes'
  | 'menus'
  | 'users'
  | 'support';

function BackLink() {
  return (
    <Link
      component={RouterLink}
      to="/tenants"
      underline="hover"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 1.5 }}
    >
      <ArrowBackRounded sx={{ fontSize: 16 }} /> All tenants
    </Link>
  );
}

function SuspendResume({ tenant }: { tenant: TenantDetail }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const suspended = tenant.subscription?.suspended ?? false;
  if (!tenant.subscription) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: tenantKeys.all });
  return (
    <>
      <Button
        variant="outlined"
        color={suspended ? 'primary' : 'error'}
        startIcon={suspended ? <PlayArrowRounded /> : <BlockRounded />}
        onClick={() => setOpen(true)}
      >
        {suspended ? 'Resume' : 'Suspend'}
      </Button>
      <ReasonDialog
        open={open}
        onClose={() => setOpen(false)}
        title={suspended ? `Resume ${tenant.name}?` : `Suspend ${tenant.name}?`}
        danger={!suspended}
        confirmLabel={suspended ? 'Resume shop' : 'Suspend shop'}
        reasonRequired={!suspended}
        body={
          suspended
            ? 'The suspension is lifted now. The shop returns to the state its paid-through date gives it (active, grace or locked).'
            : 'The shop is locked immediately, whatever its paid-through date: staff can still sign in, view and export, but cannot post sales, payments, stock or master-data changes. Queued desktop data is kept.'
        }
        onConfirm={async (reason) => {
          const path = { path: { id: tenant.id } };
          if (suspended)
            await unwrap(
              api.POST('/platform/tenants/{id}/resume', {
                params: path,
                body: reason ? { reason } : {},
              }),
            );
          else
            await unwrap(
              api.POST('/platform/tenants/{id}/suspend', { params: path, body: { reason } }),
            );
          notify.success(suspended ? 'Shop resumed' : 'Shop suspended');
          void refresh();
        }}
      />
    </>
  );
}

function Header({ tenant }: { tenant: TenantDetail }) {
  const { can } = useAuth();
  const [extending, setExtending] = useState(false);
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between', mb: 2.5 }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
        <TenantAvatar name={tenant.name} size={52} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" component="h1" sx={{ wordBreak: 'break-word' }}>
            {tenant.name}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}
          >
            <Typography variant="body2" color="text.secondary" className="tabular">
              {tenant.slug}
            </Typography>
            <TenantStatusChip tenant={tenant} />
          </Stack>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1.25 }}>
        {can('tenants.backup') && <TenantBackupButton tenant={tenant} />}
        <MessageButtons tenant={tenant} />
        {can('tenants.suspend') && <SuspendResume tenant={tenant} />}
        {can('subscriptions.manage') && tenant.subscription && (
          <Button
            variant="contained"
            startIcon={<EventRepeatRounded />}
            onClick={() => setExtending(true)}
          >
            Extend
          </Button>
        )}
      </Stack>
      {tenant.subscription && (
        <ExtendDialog
          open={extending}
          tenant={tenant}
          subscription={tenant.subscription}
          onClose={() => setExtending(false)}
        />
      )}
    </Stack>
  );
}

function TenantCodes({ tenant }: { tenant: TenantDetail }) {
  const [state, setState] = useState<CodesState>({ status: 'ALL', page: 1, pageSize: 10 });
  return (
    <CodesPanel
      tenant={{ id: tenant.id, name: tenant.name }}
      state={state}
      onStateChange={(next) => setState((s) => ({ ...s, ...next }))}
    />
  );
}

function Detail({ id }: { id: string }) {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const detail = useQuery({ queryKey: tenantKeys.detail(id), queryFn: () => fetchTenant(id) });

  const tabs: { value: TabKey; label: string }[] = [
    { value: 'overview', label: 'Overview' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'foodpanda', label: 'Foodpanda' },
    { value: 'email', label: 'Email' },
    { value: 'messages', label: 'Messages' },
    ...(can('codes.manage') ? [{ value: 'codes' as const, label: 'Activation codes' }] : []),
    { value: 'menus', label: 'Menus' },
    { value: 'users', label: 'Users' },
    { value: 'support', label: 'Support' },
  ];
  const tab = tabs.find((t) => t.value === params.get('tab'))?.value ?? 'overview';

  if (detail.isPending)
    return (
      <>
        <BackLink />
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
          <Skeleton variant="rounded" width={52} height={52} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="40%" height={40} />
            <Skeleton width="20%" />
          </Box>
        </Stack>
        <Skeleton variant="rounded" height={320} />
      </>
    );

  if (detail.isError) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <>
        <BackLink />
        <Card>
          {notFound ? (
            <EmptyState
              icon={<StorefrontOutlined />}
              title="Shop not found"
              body="It may have been removed, or the link is wrong."
            />
          ) : (
            <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
          )}
        </Card>
      </>
    );
  }

  const tenant = detail.data;
  return (
    <>
      <BackLink />
      <Header tenant={tenant} />
      <Tabs
        value={tab}
        onChange={(_, v: TabKey) =>
          setParams(v === 'overview' ? {} : { tab: v }, { replace: true })
        }
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
      {tab === 'overview' && <OverviewTab tenant={tenant} />}
      {tab === 'subscription' && <SubscriptionTab tenant={tenant} />}
      {tab === 'whatsapp' && <WhatsappTenantTab tenant={tenant} />}
      {tab === 'foodpanda' && <PluginTenantTab tenant={tenant} plugin="foodpanda" />}
      {tab === 'email' && <PluginTenantTab tenant={tenant} plugin="email" />}
      {tab === 'messages' && <TenantMessagesTab tenant={tenant} />}
      {tab === 'codes' && <TenantCodes tenant={tenant} />}
      {tab === 'menus' && <MenusTab tenant={tenant} />}
      {tab === 'users' && <UsersTab tenant={tenant} />}
      {tab === 'support' && <SupportTab tenant={tenant} />}
      <ActivityTimeline tenantId={tenant.id} />
    </>
  );
}

export function TenantDetailPage() {
  const { id = '' } = useParams();
  return (
    <RequireCapability anyOf={['tenants.view']} what="tenants">
      <Detail key={id} id={id} />
    </RequireCapability>
  );
}
